import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Checkbox as CheckboxV2 } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	ChangePageSizeButton,
	Header,
} from '@dealstack/frontend/shared/components/Pagination/ContextualMenu';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { PAGINATION_LIMIT_PER_PAGE } from '@dealstack/frontend/shared/other/react/hooks/usePagination';
import { CssValue } from '@dealstack/shared/lib/frontend/common-ui/types';
import { em, rem } from '@mantine/core';
import { forwardRef, memo } from 'react';
import styled from 'styled-components';
import { PredefinedTextVariants } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { PredefinedV2TextVariants } from '@dealstack/frontend/shared/components/TextV2/TextV2.types';
import type { ISearchSelectBar } from './SearchSelectBar.types';

type SearchSelectBarV2SizeValues = keyof typeof SearchSelectBarV2Size;
enum SearchSelectBarV2Size {
	s = 's',
	m = 'm',
	l = 'l',
}

type ISizeVariantMapping = {
	[key in SearchSelectBarV2SizeValues]: {
		optionTextVariant: PredefinedV2TextVariants;
		messageTextVariant: PredefinedV2TextVariants;
		headerTextVariant: PredefinedV2TextVariants;
		width: CssValue;
		height: CssValue;
		inputIconSize: {
			width: CssValue;
			height: CssValue;
		};
	};
};

const sizeVariantMapping: ISizeVariantMapping = {
	[SearchSelectBarV2Size.s]: {
		optionTextVariant: 'sans/11/400',
		messageTextVariant: 'sans/14/400',
		headerTextVariant: 'sans/11/400',
		width: `12.5rem`,
		height: `13.75rem`,
		inputIconSize: {
			width: `1rem`,
			height: `1rem`,
		},
	},
	[SearchSelectBarV2Size.m]: {
		optionTextVariant: 'sans/14/400',
		messageTextVariant: 'sans/14/400',
		headerTextVariant: 'sans/14/400',
		width: `15rem`,
		height: `16.25rem`,
		inputIconSize: {
			width: `1.5rem`,
			height: `1.5rem`,
		},
	},
	[SearchSelectBarV2Size.l]: {
		optionTextVariant: 'sans/17/400',
		messageTextVariant: 'sans/17/400',
		headerTextVariant: 'sans/17/400',
		width: `20rem`,
		height: `18rem`,
		inputIconSize: {
			width: `2rem`,
			height: `2rem`,
		},
	},
};
const SearchSelectBar = forwardRef<HTMLDivElement, ISearchSelectBar>(
	(
		{
			className,
			options,
			updateSelectedOptions,
			checkVariantOfCheckbox,
			searchSelectBarInputName,
			showCheckbox,
			showHeader,
			showExtra,
			showSearchBar,
			dangerOptions,
			state,
			inputPlaceHolderText,
			onLimitChange,
			onSearchInputChange,
			onSelectOption,
			size,
		},
		ref
	) => {
		const tCommonUi = useTranslationCommonUi();

		const onChangeAmountPerPage = () => {
			if (!onLimitChange) {
				return;
			}
			switch (state?.limit) {
				case PAGINATION_LIMIT_PER_PAGE.x25: {
					return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x50);
				}
				case PAGINATION_LIMIT_PER_PAGE.x50: {
					return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x100);
				}
				case PAGINATION_LIMIT_PER_PAGE.x100: {
					return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x25);
				}

				default:
					return onLimitChange(PAGINATION_LIMIT_PER_PAGE.x25);
			}
		};
		const availableLimitsPerPage = [
			PAGINATION_LIMIT_PER_PAGE.x25,
			PAGINATION_LIMIT_PER_PAGE.x50,
			PAGINATION_LIMIT_PER_PAGE.x100,
		];
		return (
			<div className={className} ref={ref}>
				{showHeader && (
					<HeaderText
						variant={
							sizeVariantMapping[size].headerTextVariant as PredefinedTextVariants
						}
						disableVariantMapping
					>
						{tCommonUi('contextual_menu.showing')}
					</HeaderText>
				)}
				{showSearchBar && (
					<StringInputV2
						leftIcon="search-v3"
						name={searchSelectBarInputName ?? ''}
						inputPlaceHolder={inputPlaceHolderText}
						onInputChange={onSearchInputChange}
						size={size}
					/>
				)}
				{options?.length === 0 && (
					<MessageWrapper
						variant={
							sizeVariantMapping[size].messageTextVariant as PredefinedTextVariants
						}
						disableVariantMapping
					>
						{tCommonUi('no_results_were_found')}
					</MessageWrapper>
				)}
				{!!options && options.length > 0 && (
					<OptionsWrapper>
						{options.map((option) => (
							<OptionWrapper
								key={option.value}
								onClick={() => {
									return (
										(option.onClick && option.onClick()) ??
										(onSelectOption && onSelectOption(option.value))
									);
								}}
							>
								{showCheckbox && (
									<CheckboxV2
										variant={
											(checkVariantOfCheckbox &&
												checkVariantOfCheckbox(option.value)) ??
											'off'
										}
										size="s"
										onClick={() =>
											updateSelectedOptions &&
											updateSelectedOptions(option.value)
										}
										disabled={option.disabled}
									/>
								)}
								<UIElementIdentifier
									identifier={
										option.UIElementIdentifier ?? TEST_ELEMENT_IDS.MISSING
									}
								>
									<OptionText
										variant={
											sizeVariantMapping[size]
												.optionTextVariant as PredefinedTextVariants
										}
										disableVariantMapping
										className={option.disabled ? 'disabled-checkbox' : ''}
									>
										{option.label}
									</OptionText>
								</UIElementIdentifier>
							</OptionWrapper>
						))}
						{!!dangerOptions && dangerOptions.length > 0 && (
							<DangerOptionsWrapper>
								{dangerOptions.map((option) => (
									<OptionWrapper
										key={option.value}
										onClick={() => {
											return (
												(option.onClick && option.onClick()) ??
												(onSelectOption && onSelectOption(option.value))
											);
										}}
									>
										{showCheckbox && (
											<CheckboxV2
												variant={
													(checkVariantOfCheckbox &&
														checkVariantOfCheckbox(option.value)) ??
													'off'
												}
												size="s"
												onClick={() =>
													updateSelectedOptions &&
													updateSelectedOptions(option.value)
												}
												disabled={option.disabled}
											/>
										)}
										<UIElementIdentifier
											identifier={
												option.UIElementIdentifier ??
												TEST_ELEMENT_IDS.MISSING
											}
										>
											<DangerOptionText
												variant={
													sizeVariantMapping[size]
														.optionTextVariant as PredefinedTextVariants
												}
												disableVariantMapping
												className={
													option.disabled ? 'disabled-checkbox' : ''
												}
											>
												{option.label}
											</DangerOptionText>
										</UIElementIdentifier>
									</OptionWrapper>
								))}
							</DangerOptionsWrapper>
						)}
					</OptionsWrapper>
				)}
				{showExtra && (
					<ShowExtraWrapper>
						<Header disableVariantMapping variant="sans/09/600">
							{tCommonUi('contextual_menu.showing')}
						</Header>
						<ChangePageSizeButton
							onClick={onChangeAmountPerPage}
							currentLimitPerPage={state?.limit || 0}
							availableLimits={availableLimitsPerPage}
						/>
					</ShowExtraWrapper>
				)}
			</div>
		);
	}
);
SearchSelectBar.displayName = 'SearchSelectBar';

const HeaderText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
	padding: ${rem(8)} 0;
`;
const MessageWrapper = styled(Text)`
	padding: 2px;
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;

const DangerOptionsWrapper = styled.div`
	border-top: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	margin-top: ${rem(5)};
`;

const ShowExtraWrapper = styled.div`
	border-top: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
`;

const OptionsWrapper = styled.div`
	--scroll-bar-color: ${({ theme }) => theme.v2.color.neutral[400]};
	gap: ${em(8)};

	::-webkit-scrollbar {
		width: ${em(2)};
		height: ${em(80)};
	}

	::-webkit-scrollbar-thumb {
		border-radius: 1px solid var(--scroll-bar-color);
		background: var(--scroll-bar-color);
	}
`;

const OptionWrapper = styled.div`
	display: flex;
	gap: ${em(8)};
	align-items: center;
	padding: ${em(4)};
	border-radius: ${rem(4)};
	cursor: pointer;
`;

const OptionText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;
const DangerOptionText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.error[800]};
`;
const Memoized = memo(SearchSelectBar);
const Styled = styled(Memoized)`
	width: ${({ size }) => sizeVariantMapping[size].width};
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	padding: ${rem(8)};
	border-radius: ${rem(4)};
	box-shadow: 0 ${rem(2)} ${rem(4)} 0 ${({ theme }) => theme.v2.color.neutral[300]};
	box-sizing: border-box;
	${({ showScrollBar, size }) =>
		showScrollBar &&
		`${OptionsWrapper} {
            max-height:  ${sizeVariantMapping[size].height};
            overflow: auto;
        }
    `};

	.disabled-checkbox {
		color: ${({ theme }) => theme.v2.color.neutral[500]};
	}

	${Icon} {
		${({ size }) => sizeVariantMapping[size].inputIconSize}
	}

	${ChangePageSizeButton} {
		width: 100%;
	}

	${ButtonV2} {
		padding: 0;
		background-color: ${({ theme }) => theme.v2.color.neutral['100']};
		&:focus {
			border: none;
		}
	}
`;

export { Styled as SearchSelectBar };
